import { create } from "zustand";
import { serverClock } from "../lib/castSync/serverClock";
import { getGcStreamSlots } from "../api/stream";
import type { RBEvent } from "../hooks/useRabbitMQ";
import type {
  PlaybackState,
  SlotEntry,
  StreetChatStreamInfo,
} from "../types/stream";
import {
  SLOT_FIELDS,
  getSlotById,
  hasStreaming,
  keepNewerTimelines,
  isLiveStreamSlot,
  slotEntries,
  updateStreamSlot,
} from "../lib/streamSlot";
import logger from "../lib/logger";

export interface StreamStatusState {
  /**
   * Slot state per group conversation, unflattened. Every conversation the user
   * has opened stays here, kept current by events, so a mini player that
   * outlives its conversation still has live data. Only `reset()` clears it.
   */
  infoByGc: Record<number, StreetChatStreamInfo>;
  /** The conversation the user has open. */
  stcId: number | null;
  gcId: number | null;
  /** The gcId whose fetch has settled, successfully or not. */
  loadedGcId: number | null;
  /** Local media element state, keyed by slot id (unique across conversations). */
  playback: Record<number, PlaybackState>;
  /** Whether each conversation in the user's street chat has anything live. */
  liveByGc: Record<number, boolean>;
  /**
   * Live slot ids seen via events for a conversation NOT in `infoByGc` — the
   * user has never opened it, so there is no full slot info to recompute
   * `hasStreaming` from. Without this, a second START and a single STOP would
   * incorrectly clear `liveByGc` for a gc that still has another live slot.
   * Replaced, never mutated. Dropped for a gc the moment it becomes cached
   * (`setInfo`/`refresh`/an event with an `owner`), since `hasStreaming` on
   * the cached info takes over from there.
   */
  liveSlotIdsByGc: Record<number, number[]>;
  /**
   * Uncached gcs the cold-start summary reported live, with no slot ids to
   * track yet. Stays live until the first STOP event for that gc, which
   * clears the marker — from then on liveness for that gc is judged solely by
   * `liveSlotIdsByGc`.
   */
  unknownLiveGcs: Record<number, true>;

  load: (stcId: number, gcId: number) => Promise<void>;
  /**
   * The guarded fetch: gets fresh slots for `gcId`, merges through anything an
   * event touched while this fetch was in flight, and writes the result.
   * Never touches `stcId`/`gcId`/`loadedGcId` — `load()` owns those. Exposed
   * so a caller with its own bookkeeping (the PiP store's watchdog poll) can
   * refresh a conversation's cache without stealing "the conversation the
   * user has open".
   */
  refresh: (stcId: number, gcId: number) => Promise<void>;
  setInfo: (gcId: number, info: StreetChatStreamInfo) => void;
  setLiveByGc: (liveByGc: Record<number, boolean>) => void;
  applyEvent: (event: RBEvent) => void;
  setPlayback: (slotId: number, playback: PlaybackState) => void;
  reset: () => void;
}

/** The only behaviours that justify a refetch — none of them carry a slot. */
const REFETCH_BEHAVIOURS = new Set([
  "STREAM_PERMISSION_GRANTED",
  "STREAM_PERMISSION_REMOVED",
  "BY_STC_CHANGED",
]);

const initial = {
  infoByGc: {} as Record<number, StreetChatStreamInfo>,
  stcId: null as number | null,
  gcId: null as number | null,
  loadedGcId: null as number | null,
  playback: {} as Record<number, PlaybackState>,
  liveByGc: {} as Record<number, boolean>,
  liveSlotIdsByGc: {} as Record<number, number[]>,
  unknownLiveGcs: {} as Record<number, true>,
};

/** Returns `record` unchanged if it has no entry for `key`, else a copy without it. */
function dropKey<T>(
  record: Record<number, T>,
  key: number,
): Record<number, T> {
  if (!(key in record)) return record;
  const next = { ...record };
  delete next[key];
  return next;
}

/**
 * Slot IDs touched by events while a fetch for their gc is in flight — one
 * tracking window per concurrent fetch, so a poll and a load for the same gc
 * (now routine: the PiP store's watchdog poll refreshes through this store
 * while a panel's own `load()` may still be settling) can't clobber each
 * other's protection. `refresh()` opens its own window right before fetching
 * and closes only that window in its `finally`; `applyEvent` adds a touch to
 * every window currently open for that gc. When a fetch resolves, slots its
 * own window recorded keep their event-applied values instead of the fetch's
 * answer. `reset()` clears the whole map, since none of it should outlive a
 * reset (e.g. logout).
 * Map key: gcId, Map value: the set of open tracking windows for that gc —
 * each window itself a Set of the slot IDs touched during its fetch.
 */
const touchedSlotsPerGc = new Map<number, Set<Set<number>>>();

type SlotsLoadedListener = (gcId: number, info: StreetChatStreamInfo) => void;
const slotsLoadedListeners = new Set<SlotsLoadedListener>();

/**
 * Fired after every full slot load `refresh` wrote (not on a failed fetch), with the info as
 * written: the own-cast orphan reconcile runs on it (Android `reconcileOwnCasts`).
 */
export function onSlotsLoaded(listener: SlotsLoadedListener): () => void {
  slotsLoadedListeners.add(listener);
  return () => {
    slotsLoadedListeners.delete(listener);
  };
}

/** Opens a new touched-slot tracking window for one fetch of `gcId`. */
function openTouchWindow(gcId: number): Set<number> {
  const touched = new Set<number>();
  let windows = touchedSlotsPerGc.get(gcId);
  if (!windows) {
    windows = new Set();
    touchedSlotsPerGc.set(gcId, windows);
  }
  windows.add(touched);
  return touched;
}

/** Closes one fetch's tracking window. Any other window still open for `gcId` is untouched. */
function closeTouchWindow(gcId: number, touched: Set<number>): void {
  const windows = touchedSlotsPerGc.get(gcId);
  if (!windows) return;
  windows.delete(touched);
  if (windows.size === 0) touchedSlotsPerGc.delete(gcId);
}

export const useStreamStatusStore = create<StreamStatusState>((set, get) => ({
  ...initial,

  load: async (stcId, gcId) => {
    set({ stcId, gcId });
    try {
      await get().refresh(stcId, gcId);
    } finally {
      // Settled either way: no slots are coming. Only for the conversation
      // still open — a slow fetch for one the user has left must not mark the
      // current one loaded.
      if (get().gcId === gcId) set({ loadedGcId: gcId });
    }
  },

  refresh: async (stcId, gcId) => {
    // Open tracking for this fetch's duration only — a fresh, empty window
    // that coexists with any other fetch already in flight for this gc (e.g.
    // a watchdog poll racing a panel's `load()`).
    const touched = openTouchWindow(gcId);
    try {
      const info = await getGcStreamSlots(stcId, gcId);
      // Merge fetched info with current state, but slots touched by events
      // during this fetch win over the fetch.
      const state = get();
      let merged: StreetChatStreamInfo = info;
      if (touched.size > 0 && state.infoByGc[gcId]) {
        merged = { ...info };
        // For each slot the current state holds, if an event touched it
        // during this fetch, keep the current value; otherwise use the
        // fetched value.
        for (const entry of slotEntries(state.infoByGc[gcId])) {
          if (!touched.has(entry.slot.id)) continue;
          const pair = SLOT_FIELDS.find(([localId]) => localId === entry.localId);
          if (pair) merged[pair[1]] = entry.slot;
        }
      }
      set((s) => {
        merged = keepNewerTimelines(merged, s.infoByGc[gcId]);
        return {
          infoByGc: { ...s.infoByGc, [gcId]: merged },
          liveByGc: { ...s.liveByGc, [gcId]: hasStreaming(merged) },
          // Now cached: hasStreaming(merged) is authoritative for this gc.
          liveSlotIdsByGc: dropKey(s.liveSlotIdsByGc, gcId),
          unknownLiveGcs: dropKey(s.unknownLiveGcs, gcId),
        };
      });
      const written = get().infoByGc[gcId];
      for (const listener of [...slotsLoadedListeners]) {
        try {
          listener(gcId, written);
        } catch (err) {
          logger.error("[StreamStatus] slots-loaded listener failed", err);
        }
      }
    } catch (err) {
      logger.warn("[StreamStatus] Slot fetch failed for gc", gcId, err);
    } finally {
      // This fetch has settled — only its own tracking window closes. A
      // sibling fetch (a poll, another load) still in flight for this gc
      // keeps its own window open.
      closeTouchWindow(gcId, touched);
    }
  },

  setInfo: (gcId, incoming) =>
    set((state) => {
      const info = keepNewerTimelines(incoming, state.infoByGc[gcId]);
      return {
        infoByGc: { ...state.infoByGc, [gcId]: info },
        liveByGc: { ...state.liveByGc, [gcId]: hasStreaming(info) },
        // Now cached: hasStreaming(info) is authoritative for this gc.
        liveSlotIdsByGc: dropKey(state.liveSlotIdsByGc, gcId),
        unknownLiveGcs: dropKey(state.unknownLiveGcs, gcId),
      };
    }),

  setLiveByGc: (fromServer) =>
    set((state) => {
      // The server's snapshot replaces what we had — except for conversations
      // held in full, which events keep current and may be newer than it.
      const liveByGc = { ...fromServer };
      let unknownLiveGcs = state.unknownLiveGcs;
      for (const key of Object.keys(fromServer)) {
        const gc = Number(key);
        // A cached conversation's liveness is recomputed below regardless;
        // only an uncached gc needs the "unknown live" marker.
        if (!fromServer[gc] || state.infoByGc[gc]) continue;
        if (!unknownLiveGcs[gc]) unknownLiveGcs = { ...unknownLiveGcs, [gc]: true };
        liveByGc[gc] = (state.liveSlotIdsByGc[gc]?.length ?? 0) > 0 || true;
      }
      for (const key of Object.keys(state.infoByGc)) {
        const gc = Number(key);
        liveByGc[gc] = hasStreaming(state.infoByGc[gc]);
      }
      return { liveByGc, unknownLiveGcs };
    }),

  applyEvent: (event) => {
    if (event.action !== "STC_EVENT" && event.action !== "GC_EVENT") return;
    const { activities = [], slot } = event.metadata;
    if (typeof event.metadata.serverTime === "number") {
      serverClock.noteServerTime(event.metadata.serverTime);
    }

    if (slot != null) {
      // Without an id there is no telling which slot to update.
      if (slot.id == null) return;
      // Deliberately no refetch, whatever the behaviour. START_STREAM,
      // STOP_STREAM and STREAM_MEDIA_* all carry the full slot, and a refetch
      // can land after a STOP_STREAM merge and overwrite available:true with
      // stale available:false.
      const state = get();
      const infoByGc = { ...state.infoByGc };
      let owner: number | null = null;
      for (const key of Object.keys(infoByGc)) {
        const gc = Number(key);
        const merged = updateStreamSlot(infoByGc[gc], slot);
        if (merged !== infoByGc[gc]) {
          infoByGc[gc] = merged;
          owner = gc;
          // Slot ids are database ids, unique across conversations.
          break;
        }
      }
      // Track this slot as touched in every fetch window open for its gc
      // right now (refresh() opens one per fetch; there may be none, or more
      // than one — a load and a poll can overlap), so none of them overwrites
      // it — and an ordinary event with no fetch pending doesn't poison a
      // later, unrelated refetch.
      if (owner != null) {
        for (const touched of touchedSlotsPerGc.get(owner) ?? [])
          touched.add(slot.id);
      }
      // The event names one slot. A conversation is live if ANY of its slots
      // is, so for one we hold in full, recompute from all of them.
      const liveByGc = { ...state.liveByGc };
      let liveSlotIdsByGc = state.liveSlotIdsByGc;
      let unknownLiveGcs = state.unknownLiveGcs;
      if (owner != null) {
        liveByGc[owner] = hasStreaming(infoByGc[owner]);
        // Now cached: hasStreaming(infoByGc[owner]) is authoritative from here.
        liveSlotIdsByGc = dropKey(liveSlotIdsByGc, owner);
        unknownLiveGcs = dropKey(unknownLiveGcs, owner);
      } else if (slot.gcId != null) {
        // No cached info for this gc — track live slot ids by hand so a
        // second still-live slot survives one of its siblings stopping.
        const gc = slot.gcId;
        const isLive = !slot.available;
        const currentIds = liveSlotIdsByGc[gc] ?? [];
        let nextIds = currentIds;
        if (isLive) {
          if (!currentIds.includes(slot.id))
            nextIds = [...currentIds, slot.id];
        } else if (currentIds.includes(slot.id)) {
          nextIds = currentIds.filter((id) => id !== slot.id);
        }
        if (nextIds !== currentIds)
          liveSlotIdsByGc = { ...liveSlotIdsByGc, [gc]: nextIds };
        // The first STOP for this gc clears the summary's "unknown live"
        // marker — from then on liveness is judged solely by tracked ids.
        if (!isLive && unknownLiveGcs[gc]) unknownLiveGcs = dropKey(unknownLiveGcs, gc);
        liveByGc[gc] = nextIds.length > 0 || !!unknownLiveGcs[gc];
      }
      set({ infoByGc, liveByGc, liveSlotIdsByGc, unknownLiveGcs });
      return;
    }

    if (event.action !== "STC_EVENT") return;
    if (!activities.some((a) => REFETCH_BEHAVIOURS.has(a.behavior))) return;
    const { stcId, gcId } = get();
    if (stcId != null && gcId != null) void get().load(stcId, gcId);
  },

  setPlayback: (slotId, playback) => {
    if (get().playback[slotId] === playback) return;
    set((state) => ({ playback: { ...state.playback, [slotId]: playback } }));
  },

  reset: () => {
    // Forget any in-flight-load bookkeeping too — none of it should outlive
    // a reset (e.g. logout), and an abandoned fetch that resolves later must
    // not find stale tracking waiting for it.
    touchedSlotsPerGc.clear();
    set({ ...initial });
  },
}));

export interface LocatedSlot extends SlotEntry {
  gcId: number;
}

/** The open conversation's slots. Returns a stored reference — safe as a selector. */
export function selectInfo(s: StreamStatusState): StreetChatStreamInfo | null {
  return s.gcId == null ? null : (s.infoByGc[s.gcId] ?? null);
}

/** Anything live anywhere in the user's street chat. Primitive — safe as a selector. */
export function selectAnyLive(s: StreamStatusState): boolean {
  return (
    Object.values(s.liveByGc).some(Boolean) || hasStreaming(selectInfo(s))
  );
}

/**
 * A slot's full status: where it lives, what the server says, what the local
 * player is doing. Builds a new object — call on `getState()`, never as a hook
 * selector.
 */
export function selectSlotStatus(
  s: StreamStatusState,
  slotId: number,
): { entry: LocatedSlot | undefined; playback: PlaybackState } {
  return {
    entry: findSlot(s.infoByGc, slotId),
    playback: s.playback[slotId] ?? "idle",
  };
}

/** The slot with this id, in whichever cached conversation holds it. */
export function findSlot(
  infoByGc: Record<number, StreetChatStreamInfo>,
  slotId: number,
): LocatedSlot | undefined {
  for (const key of Object.keys(infoByGc)) {
    const gcId = Number(key);
    const entry = getSlotById(infoByGc[gcId], slotId);
    if (entry) return { ...entry, gcId };
  }
  return undefined;
}

/**
 * Live VIDEO and AUDIO slots in the same conversation as `slotId`, in slot
 * order — what the mini player's prev/next steps through. Read from the live
 * cache, so an ended stream can never be offered.
 */
export function liveSiblings(
  infoByGc: Record<number, StreetChatStreamInfo>,
  slotId: number,
): SlotEntry[] {
  const found = findSlot(infoByGc, slotId);
  if (!found) return [];
  return slotEntries(infoByGc[found.gcId]).filter(
    (entry) => isLiveStreamSlot(entry.localId) && !entry.slot.available,
  );
}
